type SavedEdits = {
  // False when a save failed. True for a card with no edits.
  readonly saved: boolean;
  // The revision the server gave the save that was made or awaited, which holds the user's edit. Null when none was.
  readonly revision: number | null;
};

// Each card on screen registers how to save what its user typed, so a host that acts on several cards at once can
// save their edits first, rather than race the save a blur started
const savers = new Map<string, Set<() => Promise<SavedEdits>>>();

export const registerCardEditsSaver = (
  cardId: string,
  save: () => Promise<SavedEdits>
): (() => void) => {
  const forCard = savers.get(cardId) ?? new Set();
  forCard.add(save);
  savers.set(cardId, forCard);
  return () => {
    forCard.delete(save);
    if (forCard.size === 0 && savers.get(cardId) === forCard)
      savers.delete(cardId);
  };
};

/** Saves the card's unsaved edits after any save in flight. A card with none on screen has nothing to save. */
export const saveCardEdits = async (cardId: string): Promise<SavedEdits> => {
  const results = await Promise.all(
    [...(savers.get(cardId) ?? [])].map((save) => save())
  );
  const revisions = results.flatMap(({ revision }) => revision ?? []);
  return {
    saved: results.every(({ saved }) => saved),
    revision: revisions.length > 0 ? Math.max(...revisions) : null,
  };
};
